// ─── Shared cTrader Stream ────────────────────────────────────
// Single persistent SSE connection per cTrader account, multiplexed
// to all consumers. Uses Edge Function "stream-market-data" which
// opens a WebSocket to cTrader and streams events via SSE.
//
// Architecture:
//   1 persistent SSE (Edge Function WS → cTrader) → N consumers
//   Auto-reconnect when Edge Function times out (~60s), gap ~2-3s.
//   All consumers share the same symbol + period subscriptions.
//   Reconnects when consumer set changes (new symbols/periods added).
//
// Why SSE instead of polling:
//   Polling opens/closes a fresh WS every 15s → auth overhead 2-4s
//   per poll + 5s gap between polls + exponential backoff on failure.
//   Persistent SSE keeps the WS open and streams continuously for
//   ~55s, then reconnects in 2-3s. No auth overhead between reconnects,
//   data is truly streaming (not snapshot), gap is minimal.

import { supabase } from './supabase';

// ─── Types ────────────────────────────────────────────────────

export interface StreamEvent {
  type: 'spot' | 'trendbar' | 'heartbeat' | 'connected' | 'disconnected' | 'error' | 'trader_update' | 'execution' | 'positions' | 'trader' | 'margin';
  symbolId?: number | null;
  reconcileRes?: Record<string, unknown>;
  symbolRes?: Record<string, unknown> | null;
  positionCount?: number;
  traderRes?: Record<string, unknown> | null;
  marginEvent?: Record<string, unknown> | null;
  bid?: number | null;
  ask?: number | null;
  open?: number | null;
  high?: number | null;
  low?: number | null;
  close?: number | null;
  volume?: number | null;
  timestamp?: number | null;
  period?: number | null;
  receivedAt?: number;
  description?: string;
  message?: string;
  payload?: Record<string, unknown>;
  errorCode?: unknown;
  code?: number;
  reason?: string;
  accountId?: number;
  subscribedSymbols?: number[];
  subscribedPeriods?: number[];
}

export type StreamStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

export interface StreamConsumer {
  /** Unique consumer ID — used for dedup and lifecycle */
  id: string;
  /** Symbol IDs this consumer needs */
  symbolIds: number[];
  /** Trendbar periods this consumer needs (minutes). Empty = spots only. */
  periods: number[];
  /** symbolId → digits for price scaling */
  symbolDigits: Record<number, number>;
  /** Called with every stream event */
  onEvent: (event: StreamEvent) => void;
  /** Optional status change callback */
  onStatusChange?: (status: StreamStatus) => void;
}

// ─── Per-Account Stream State ─────────────────────────────────

interface AccountStream {
  accountId: number;
  consumers: Map<string, StreamConsumer>;
  abortController: AbortController | null;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  shouldStream: boolean;
  streamStatus: StreamStatus;
  streamError: string | null;
  lastTick: number;
  heartbeatWatchdogTimer: ReturnType<typeof setInterval> | null;
  restartDebounceTimer: ReturnType<typeof setTimeout> | null;
  /** Snapshot of symbolIds + periods at the time the current connection was initiated. */
  lastSymbolIds: number[];
  lastPeriods: number[];
  /** Timestamp when the current connection attempt started. */
  connectionStartTime: number;
  /** True if a restart was requested while the stream was connecting. */
  pendingRestart: boolean;
  /** Exponential backoff: consecutive error/restart count. Resets after stable connection. */
  consecutiveRestarts: number;
  /** Timer to reset consecutiveRestarts after stable period */
  stableResetTimer: ReturnType<typeof setTimeout> | null;
  /** Track the active reader so we can cancel it before aborting the controller */
  activeReader: ReadableStreamDefaultReader<Uint8Array> | null;
}

// ─── Positions Snapshot (server-driven reconcile) ───────────
// The Edge Function now reconciles positions ON the persistent SSE
// WebSocket and pushes the result as a `positions` SSE event. We cache
// the latest snapshot per account so on-demand consumers (copy-trade
// engine, Chin Bot order loops) can read positions without opening a
// second cTrader WebSocket.

export interface PositionsSnapshot {
  accountId: number;
  reconcileRes: Record<string, unknown>;
  symbolRes: Record<string, unknown> | null;
  positionCount: number;
  timestamp: number;
}

// ─── Account (trader/margin) Snapshot ────────────────────────
// The Edge Function now also requests TRADER data on the same stream
// connection and pushes it as `trader` (ProtoOATRADER_RES) plus `margin`
// (ProtoOAMarginChangedEvent) SSE events. We cache the latest snapshot per
// account so on-demand consumers (trailing-stop engine, dashboard) can read
// balances/equity/margin without opening a second cTrader WebSocket.

export interface AccountSnapshot {
  accountId: number;
  traderRes: Record<string, unknown> | null;
  marginEvent: Record<string, unknown> | null;
  timestamp: number;
}

// ─── Module State ─────────────────────────────────────────────

const accountStreams = new Map<number, AccountStream>();
const latestPositionsByAccount = new Map<number, PositionsSnapshot>();
const latestAccountByAccount = new Map<number, AccountSnapshot>();

/**
 * Get the latest positions snapshot for an account, if the shared
 * stream has pushed one. Returns undefined when no stream is running
 * or no reconcile has completed yet.
 */
export function getLatestPositionsSnapshot(accountId: number): PositionsSnapshot | undefined {
  return latestPositionsByAccount.get(accountId);
}

/**
 * Get the latest trader/margin snapshot for an account, if the shared
 * stream has pushed one. Returns undefined when no stream is running or
 * no trader data has arrived yet.
 */
export function getLatestAccountSnapshot(accountId: number): AccountSnapshot | undefined {
  return latestAccountByAccount.get(accountId);
}

// ─── Aggregation Helpers ─────────────────────────────────────

function aggregateSymbolIds(consumers: Map<string, StreamConsumer>): number[] {
  const set = new Set<number>();
  consumers.forEach((c) => c.symbolIds.forEach((s) => set.add(s)));
  return [...set];
}

function aggregatePeriods(consumers: Map<string, StreamConsumer>): number[] {
  const set = new Set<number>();
  consumers.forEach((c) => c.periods.forEach((p) => set.add(p)));
  return [...set];
}

function aggregateDigits(consumers: Map<string, StreamConsumer>): Record<number, number> {
  const digits: Record<number, number> = {};
  consumers.forEach((c) => {
    Object.entries(c.symbolDigits).forEach(([symId, d]) => {
      const id = Number(symId);
      if (digits[id] === undefined || d > digits[id]) {
        digits[id] = d;
      }
    });
  });
  return digits;
}

// ─── Restart Debounce ─────────────────────────────────────

function scheduleStreamRestart(stream: AccountStream, immediate = false): void {
  if (stream.restartDebounceTimer) {
    clearTimeout(stream.restartDebounceTimer);
    stream.restartDebounceTimer = null;
  }

  // If stream is currently connecting and it's been <10s, defer restart
  if (stream.streamStatus === 'connecting') {
    const elapsed = Date.now() - stream.connectionStartTime;
    if (elapsed < 10000) {
      console.log(
        `[SharedStream] ⏸ Deferring restart — stream is connecting (${elapsed}ms elapsed), ` +
        `will re-evaluate after connection resolves`
      );
      stream.pendingRestart = true;
      return;
    }
    console.warn(`[SharedStream] ⚡ Stream connecting for ${elapsed}ms — allowing forced restart`);
  }

  // Exponential backoff for non-immediate restarts
  const BASE_COOLDOWN = 2000; // SSE reconnects are fast (2s base)
  const backoffMultiplier = Math.pow(2, Math.min(stream.consecutiveRestarts, 4));
  const effectiveCooldown = Math.min(BASE_COOLDOWN * backoffMultiplier, 15000);

  const sinceLastStart = Date.now() - stream.connectionStartTime;
  if (!immediate && stream.streamStatus !== 'idle' && sinceLastStart < effectiveCooldown) {
    const remaining = effectiveCooldown - sinceLastStart;
    console.log(
      `[SharedStream] ⏸ Backoff — restart deferred ${remaining}ms ` +
      `(consecutive=${stream.consecutiveRestarts} cooldown=${effectiveCooldown}ms)`
    );
    stream.restartDebounceTimer = setTimeout(() => {
      stream.restartDebounceTimer = null;
      if (stream.shouldStream && stream.consumers.size > 0) {
        startAccountStream(stream);
      }
    }, remaining + 200);
    return;
  }

  // Clear any pending restart flag
  stream.pendingRestart = false;

  if (stream.shouldStream && stream.consumers.size > 0) {
    const delay = immediate ? 0 : Math.max(200, effectiveCooldown - sinceLastStart);
    if (delay > 0) {
      stream.restartDebounceTimer = setTimeout(() => {
        stream.restartDebounceTimer = null;
        startAccountStream(stream);
      }, delay);
    } else {
      startAccountStream(stream);
    }
  }
}

// ─── SSE Stream Lifecycle ─────────────────────────────────────

async function startAccountStream(stream: AccountStream): Promise<void> {
  // ── Gracefully cancel the active reader BEFORE aborting the controller ──
  // This prevents "BodyStreamBuffer was aborted" unhandled rejections in Chrome.
  if (stream.activeReader) {
    await stream.activeReader.cancel().catch(() => {});
    stream.activeReader = null;
  }
  // Cleanup previous connection
  if (stream.abortController) {
    try { stream.abortController.abort(); } catch { /* defensive */ }
    stream.abortController = null;
  }
  if (stream.reconnectTimer) {
    clearTimeout(stream.reconnectTimer);
    stream.reconnectTimer = null;
  }
  if (stream.heartbeatWatchdogTimer) {
    clearInterval(stream.heartbeatWatchdogTimer);
    stream.heartbeatWatchdogTimer = null;
  }

  const symbolIds = aggregateSymbolIds(stream.consumers);
  const periods = aggregatePeriods(stream.consumers);
  const symbolDigits = aggregateDigits(stream.consumers);

  // NOTE: we intentionally do NOT early-return on empty symbols anymore.
  // The copy-trade engine subscribes with an empty symbol list and relies on
  // the Edge Function's server-driven reconcile to bootstrap the symbol set
  // from open positions. Connecting with zero symbols still authenticates the
  // account and streams execution events + reconciled positions over SSE.

  stream.connectionStartTime = Date.now();
  stream.lastSymbolIds = [...symbolIds];
  stream.lastPeriods = [...periods];
  stream.consecutiveRestarts++;
  if (stream.stableResetTimer) {
    clearTimeout(stream.stableResetTimer);
    stream.stableResetTimer = null;
  }
  stream.abortController = new AbortController();
  const abortSignal = stream.abortController.signal;
  stream.streamStatus = 'connecting';
  stream.streamError = null;
  notifyConsumers(stream);

  console.log(
    `[SharedStream] 🚀 SSE CONNECT — accountId=${stream.accountId} ` +
    `symbols=${symbolIds.length} periods=${periods.join(',')} consumers=${stream.consumers.size}`
  );

  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;

    const supabaseUrl = (typeof import.meta.env !== 'undefined' && import.meta.env.VITE_PUBLIC_SUPABASE_URL)
      ? String(import.meta.env.VITE_PUBLIC_SUPABASE_URL)
      : '';
    const url = `${supabaseUrl}/functions/v1/ctrader-gateway`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({
        action: 'stream-market-data',
        accountId: stream.accountId,
        symbolIds,
        periods,
        symbolDigits,
      }),
      signal: abortSignal,
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errText.slice(0, 200)}`);
    }

    if (!response.body) {
      throw new Error('Response has no body (streaming not supported)');
    }

    // ─── Read SSE stream ───
    const reader = response.body.getReader();
    stream.activeReader = reader;
    const decoder = new TextDecoder();
    let buffer = '';
    let connectedNotified = false;
    let eventCount = 0;

    // ─── Start watchdog: if no data for 45s, force reconnect ───
    stream.heartbeatWatchdogTimer = setInterval(() => {
      if (abortSignal.aborted) return;
      if (stream.streamStatus !== 'connected') return;
      if (stream.lastTick <= 0) return;
      const staleSecs = Math.round((Date.now() - stream.lastTick) / 1000);
      if (staleSecs > 45) {
        console.warn(
          `[SharedStream] ⚡ SSE watchdog: stale ${staleSecs}s — forcing reconnect (accountId=${stream.accountId})`
        );
        if (stream.abortController) {
          try { stream.abortController.abort(); } catch { /* defensive */ }
          stream.abortController = null;
        }
        if (stream.shouldStream && stream.consumers.size > 0) {
          scheduleStreamRestart(stream, true);
        }
      }
    }, 15000);

    // ─── Read loop ───
    let readError: string | null = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log(`[SharedStream] 📡 SSE stream ended by server (accountId=${stream.accountId})`);
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          // Skip empty lines and comments (keepalive)
          if (!line || line.startsWith(':')) continue;

          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6);
            try {
              const data = JSON.parse(jsonStr) as Record<string, unknown>;
              eventCount++;
              stream.lastTick = Date.now();

              const evtType = data.type as string | undefined;

              // ─── Handle connected ───
              if (evtType === 'connected') {
                if (!connectedNotified) {
                  connectedNotified = true;
                  stream.streamStatus = 'connected';
                  stream.streamError = null;
                  console.log(
                    `[SharedStream] ✅ SSE CONNECTED — accountId=${stream.accountId} ` +
                    `(symbols=${(data.subscribedSymbols as unknown[] | undefined)?.length ?? 0} periods=${(data.subscribedPeriods as unknown[] | undefined)?.length ?? 0})`
                  );
                  deliverEvent(stream, {
                    type: 'connected',
                    accountId: stream.accountId,
                    subscribedSymbols: data.subscribedSymbols as number[] | undefined,
                    subscribedPeriods: data.subscribedPeriods as number[] | undefined,
                  });
                  notifyConsumers(stream);

                  // ─── Reset exponential backoff after 60s of stable connection ───
                  if (stream.stableResetTimer) clearTimeout(stream.stableResetTimer);
                  stream.stableResetTimer = setTimeout(() => {
                    if (stream.streamStatus === 'connected' && stream.consecutiveRestarts > 0) {
                      console.log(
                        `[SharedStream] ✅ Stable SSE for 60s — resetting backoff ` +
                        `(was at ${stream.consecutiveRestarts} consecutive restarts)`
                      );
                      stream.consecutiveRestarts = 0;
                    }
                    stream.stableResetTimer = null;
                  }, 60000);

                  // ─── Check pending restart ───
                  if (stream.pendingRestart) {
                    stream.pendingRestart = false;
                    const currentSymbols = aggregateSymbolIds(stream.consumers);
                    const currentPeriods = aggregatePeriods(stream.consumers);
                    const symbolsChanged = currentSymbols.length !== stream.lastSymbolIds.length ||
                      !currentSymbols.every((s) => stream.lastSymbolIds.includes(s));
                    const periodsChanged = currentPeriods.length !== stream.lastPeriods.length ||
                      !currentPeriods.every((p) => stream.lastPeriods.includes(p));
                    if (symbolsChanged || periodsChanged) {
                      console.log(
                        `[SharedStream] 🔄 Pending restart after connect — ` +
                        `symbols changed=${symbolsChanged} periods changed=${periodsChanged}`
                      );
                      // Cancel reader (async) and break
                      reader.cancel().catch(() => {});
                      break;
                    }
                  }
                }
              }

              // ─── Handle disconnected ───
              else if (evtType === 'disconnected') {
                console.log(
                  `[SharedStream] 📡 SSE disconnected by server — accountId=${stream.accountId} ` +
                  `code=${data.code} reason=${data.reason || ''}`
                );
                deliverEvent(stream, {
                  type: 'disconnected',
                  code: data.code as number | undefined,
                  reason: data.reason as string | undefined,
                });
                // Server-initiated disconnect — reconnect normally
                readError = `Server disconnect: ${data.reason || 'unknown'}`;
                break;
              }

              // ─── Handle error ───
              else if (evtType === 'error') {
                console.warn(
                  `[SharedStream] ⚠️ SSE error event — accountId=${stream.accountId}: ` +
                  `${data.description || data.message || 'unknown'}`
                );
                deliverEvent(stream, {
                  type: 'error',
                  description: data.description as string | undefined,
                  message: data.message as string | undefined,
                  errorCode: data.errorCode,
                });
                // Don't break — let the server decide to close or continue
              }

              // ─── Handle spot ───
              else if (evtType === 'spot') {
                deliverEvent(stream, {
                  type: 'spot',
                  symbolId: typeof data.symbolId === 'number' ? data.symbolId : null,
                  bid: typeof data.bid === 'number' ? data.bid : null,
                  ask: typeof data.ask === 'number' ? data.ask : null,
                  timestamp: typeof data.timestamp === 'number' ? data.timestamp : Date.now(),
                  receivedAt: Date.now(),
                });
              }

              // ─── Handle trendbar ───
              else if (evtType === 'trendbar') {
                deliverEvent(stream, {
                  type: 'trendbar',
                  symbolId: typeof data.symbolId === 'number' ? data.symbolId : null,
                  period: typeof data.period === 'number' ? data.period : null,
                  open: typeof data.open === 'number' ? data.open : null,
                  high: typeof data.high === 'number' ? data.high : null,
                  low: typeof data.low === 'number' ? data.low : null,
                  close: typeof data.close === 'number' ? data.close : null,
                  volume: typeof data.volume === 'number' ? data.volume : null,
                  timestamp: typeof data.timestamp === 'number' ? data.timestamp : null,
                  receivedAt: typeof data.receivedAt === 'number' ? data.receivedAt : Date.now(),
                });
              }

              // ─── Handle trader_update ───
              else if (evtType === 'trader_update') {
                deliverEvent(stream, {
                  type: 'trader_update',
                  payload: data.payload as Record<string, unknown> | undefined,
                  timestamp: typeof data.timestamp === 'number' ? data.timestamp : Date.now(),
                });
              }

              // ─── Handle execution ───
              else if (evtType === 'execution') {
                deliverEvent(stream, {
                  type: 'execution',
                  payload: data.payload as Record<string, unknown> | undefined,
                  timestamp: typeof data.timestamp === 'number' ? data.timestamp : Date.now(),
                });
              }

              // ─── Handle positions (server-driven reconcile) ───
              else if (evtType === 'positions') {
                const snapshot: PositionsSnapshot = {
                  accountId: stream.accountId,
                  reconcileRes: (data.reconcileRes as Record<string, unknown>) || {},
                  symbolRes: (data.symbolRes as Record<string, unknown> | null) || null,
                  positionCount: typeof data.positionCount === 'number' ? data.positionCount : 0,
                  timestamp: Date.now(),
                };
                latestPositionsByAccount.set(stream.accountId, snapshot);
                deliverEvent(stream, {
                  type: 'positions',
                  accountId: stream.accountId,
                  reconcileRes: snapshot.reconcileRes,
                  symbolRes: snapshot.symbolRes,
                  positionCount: snapshot.positionCount,
                  timestamp: snapshot.timestamp,
                });
              }

              // ─── Handle trader (balance/equity/margin snapshot) ───
              else if (evtType === 'trader') {
                const traderRes = (data.traderRes as Record<string, unknown>) || null;
                const existing = latestAccountByAccount.get(stream.accountId);
                latestAccountByAccount.set(stream.accountId, {
                  accountId: stream.accountId,
                  traderRes: traderRes ?? existing?.traderRes ?? null,
                  marginEvent: existing?.marginEvent ?? null,
                  timestamp: Date.now(),
                });
                deliverEvent(stream, {
                  type: 'trader',
                  accountId: stream.accountId,
                  traderRes,
                  timestamp: Date.now(),
                });
              }

              // ─── Handle margin (ProtoOAMarginChangedEvent) ───
              else if (evtType === 'margin') {
                const marginEvent = (data.marginEvent as Record<string, unknown>) || null;
                const existing = latestAccountByAccount.get(stream.accountId);
                latestAccountByAccount.set(stream.accountId, {
                  accountId: stream.accountId,
                  traderRes: existing?.traderRes ?? null,
                  marginEvent: marginEvent ?? existing?.marginEvent ?? null,
                  timestamp: Date.now(),
                });
                deliverEvent(stream, {
                  type: 'margin',
                  accountId: stream.accountId,
                  marginEvent,
                  timestamp: Date.now(),
                });
              }

              // ─── Handle heartbeat ───
              else if (evtType === 'heartbeat') {
                deliverEvent(stream, {
                  type: 'heartbeat',
                  timestamp: typeof data.ts === 'number' ? data.ts : Date.now(),
                });
              }

              // Log stats periodically
              if (eventCount % 200 === 0) {
                console.log(
                  `[SharedStream] 📊 SSE event #${eventCount} — status=${stream.streamStatus} type=${evtType || 'unknown'}`
                );
              }
            } catch {
              // Malformed JSON — skip
            }
          }
        }
      }
    } catch (err: unknown) {
      // Chrome may throw TypeError "BodyStreamBuffer was aborted" instead of AbortError
      const isAbort = (err as Error)?.name === 'AbortError'
        || (err instanceof Error && err.message?.toLowerCase().includes('aborted'))
        || abortSignal.aborted;
      if (isAbort) {
        // Intentional abort — don't treat as error
        readError = null;
      } else {
        readError = err instanceof Error ? err.message : 'Read error';
        console.warn(`[SharedStream] ⚠️ SSE read error — accountId=${stream.accountId}: ${readError}`);
      }
    } finally {
      // Clean up reader — reader.cancel() is async, must handle promise rejection
      reader.cancel().catch(() => {});
      stream.activeReader = null;

      // Clean up watchdog
      if (stream.heartbeatWatchdogTimer) {
        clearInterval(stream.heartbeatWatchdogTimer);
        stream.heartbeatWatchdogTimer = null;
      }
    }

    // ─── After stream ends ───
    if (readError && !abortSignal.aborted) {
      stream.streamStatus = 'error';
      stream.streamError = readError;
      notifyConsumers(stream);
    } else if (!abortSignal.aborted) {
      // Normal close (Edge Function timeout or server disconnect)
      // Don't mark as error — just reconnect
      stream.streamStatus = 'disconnected';
      notifyConsumers(stream);
    }

  } catch (err: unknown) {
    // Chrome may throw TypeError "BodyStreamBuffer was aborted" instead of AbortError
    const isAbort = (err as Error)?.name === 'AbortError'
      || (err instanceof Error && err.message?.toLowerCase().includes('aborted'))
      || abortSignal.aborted;
    if (isAbort) return;
    const msg = err instanceof Error ? err.message : 'SSE setup error';
    console.warn(`[SharedStream] ❌ SSE SETUP ERROR — accountId=${stream.accountId}: ${msg}`);
    stream.streamStatus = 'error';
    stream.streamError = msg;
    stream.pendingRestart = false;
    if (stream.heartbeatWatchdogTimer) {
      clearInterval(stream.heartbeatWatchdogTimer);
      stream.heartbeatWatchdogTimer = null;
    }
    notifyConsumers(stream);
  }

  // ─── Auto-reconnect ───
  // SSE connections are expected to close (Edge Function timeout ~60s).
  // Reconnect immediately with minimal delay. Only apply backoff on actual errors.
  if (stream.shouldStream && stream.consumers.size > 0 && !abortSignal.aborted) {
    const isError = stream.streamStatus === 'error';
    const delay = isError
      ? Math.min(2000 * Math.pow(2, Math.min(stream.consecutiveRestarts, 3)), 15000)
      : 500; // Normal reconnect: 500ms

    console.log(
      `[SharedStream] 🔄 Scheduling reconnect in ${delay}ms ` +
      `(status=${stream.streamStatus} error=${isError} consecutiveRestarts=${stream.consecutiveRestarts})`
    );

    stream.reconnectTimer = setTimeout(() => {
      if (stream.shouldStream && stream.consumers.size > 0) {
        startAccountStream(stream);
      }
    }, delay);
  }
}

function stopAccountStream(stream: AccountStream): void {
  stream.shouldStream = false;
  if (stream.restartDebounceTimer) {
    clearTimeout(stream.restartDebounceTimer);
    stream.restartDebounceTimer = null;
  }
  if (stream.stableResetTimer) {
    clearTimeout(stream.stableResetTimer);
    stream.stableResetTimer = null;
  }
  if (stream.heartbeatWatchdogTimer) {
    clearInterval(stream.heartbeatWatchdogTimer);
    stream.heartbeatWatchdogTimer = null;
  }
  // Cancel active reader first to prevent BodyStreamBuffer aborted errors
  if (stream.activeReader) {
    stream.activeReader.cancel().catch(() => {});
    stream.activeReader = null;
  }
  if (stream.abortController) {
    try { stream.abortController.abort(); } catch { /* defensive */ }
    stream.abortController = null;
  }
  if (stream.reconnectTimer) {
    clearTimeout(stream.reconnectTimer);
    stream.reconnectTimer = null;
  }
  stream.streamStatus = 'idle';
  stream.streamError = null;
  stream.consecutiveRestarts = 0;
  stream.pendingRestart = false;
  notifyConsumers(stream);
  accountStreams.delete(stream.accountId);
  console.log(`[SharedStream] 🛑 STOPPED — accountId=${stream.accountId}`);
}

// ─── Visibility change handler ──────────────────────────────
// When the user returns to the tab after being in the background,
// the SSE stream may have been paused/throttled by the browser.
// Force a reconnect for any account streams that have been stale
// for more than 30 seconds. Debounced: max once per 15s per tab.

let _visHandlerInstalled = false;
let _lastVisCheck = 0;

function installVisibilityHandler(): void {
  if (_visHandlerInstalled) return;
  if (typeof document === 'undefined') return;
  _visHandlerInstalled = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - _lastVisCheck < 15000) return; // debounce: 15s
    _lastVisCheck = now;

    // Check all account streams for staleness
    for (const [accountId, stream] of accountStreams) {
      if (!stream.shouldStream || stream.consumers.size === 0) continue;
      if (stream.streamStatus !== 'connected') continue;

      const staleSecs = stream.lastTick > 0
        ? Math.round((now - stream.lastTick) / 1000)
        : 999;

      if (staleSecs > 30) {
        console.warn(
          `[SharedStream] 👁 Tab visible — stream stale ${staleSecs}s ` +
          `for accountId=${accountId}, forcing reconnect...`
        );
        // Abort current and reconnect
        if (stream.abortController) {
          try { stream.abortController.abort(); } catch { /* defensive */ }
          stream.abortController = null;
        }
        // Reset consecutiveRestarts so we don't hit backoff on visibility resume
        stream.consecutiveRestarts = 0;
        scheduleStreamRestart(stream, true);
      } else {
        console.log(
          `[SharedStream] 👁 Tab visible — stream healthy (${staleSecs}s since last tick) ` +
          `for accountId=${accountId}`
        );
      }
    }
  });
}

// ─── Event Delivery ───────────────────────────────────────────

function deliverEvent(stream: AccountStream, event: StreamEvent): void {
  stream.consumers.forEach((consumer) => {
    try {
      consumer.onEvent(event);
    } catch (err) {
      console.warn(`[SharedStream] Consumer ${consumer.id} threw on event ${event.type}:`, err);
    }
  });
}

function notifyConsumers(stream: AccountStream): void {
  const status = stream.streamStatus;
  stream.consumers.forEach((consumer) => {
    try {
      consumer.onStatusChange?.(status);
    } catch {
      // ignore
    }
  });
}

// ─── Public API ───────────────────────────────────────────────

/**
 * Subscribe a consumer to the shared stream for an account.
 * Returns an unsubscribe function.
 *
 * The stream is a persistent SSE connection to the cTrader Gateway
 * Edge Function. It auto-reconnects when the Edge Function times
 * out (~60s), with a gap of ~500ms for normal reconnects and
 * exponential backoff (2s-15s) for error reconnects.
 */
export function subscribeSharedStream(consumer: StreamConsumer, accountId: number): () => void {
  // ─── Install visibility handler once (idempotent) ──────────
  installVisibilityHandler();

  let stream = accountStreams.get(accountId);

  if (!stream) {
    stream = {
      accountId,
      consumers: new Map(),
      abortController: null,
      reconnectTimer: null,
      shouldStream: true,
      streamStatus: 'idle',
      streamError: null,
      lastTick: 0,
      heartbeatWatchdogTimer: null,
      restartDebounceTimer: null,
      connectionStartTime: 0,
      pendingRestart: false,
      lastSymbolIds: [],
      lastPeriods: [],
      consecutiveRestarts: 0,
      stableResetTimer: null,
      activeReader: null,
    };
    accountStreams.set(accountId, stream);
  }

  // Check if consumer already exists
  if (stream.consumers.has(consumer.id)) {
    console.warn(`[SharedStream] Consumer ${consumer.id} already subscribed — replacing`);
  }

  stream.consumers.set(consumer.id, consumer);
  console.log(
    `[SharedStream] ➕ Consumer "${consumer.id}" subscribed — ` +
    `accountId=${accountId} total=${stream.consumers.size} ` +
    `symbols=${consumer.symbolIds.length} periods=${consumer.periods.join(',')}`
  );

  // Start/restart stream with merged subscriptions
  stream.shouldStream = true;
  const isFirstStart = stream.streamStatus === 'idle' && stream.consumers.size === 1;
  scheduleStreamRestart(stream, isFirstStart);

  // Return unsubscribe function
  return () => {
    unsubscribeSharedStream(consumer.id, accountId);
  };
}

/**
 * Get shared stream status for an account.
 */
export function getSharedStreamStatus(accountId: number): StreamStatus {
  return accountStreams.get(accountId)?.streamStatus ?? 'idle';
}

/**
 * Get the current status of every account stream (single source of truth
 * for "is the gateway connected"). Used by the login-page engine status
 * indicator so it can show a live/off signal without any auth.
 */
export function getAllStreamStatuses(): Array<{ accountId: number; status: StreamStatus }> {
  const result: Array<{ accountId: number; status: StreamStatus }> = [];
  accountStreams.forEach((stream, accountId) => {
    result.push({ accountId, status: stream.streamStatus });
  });
  return result;
}

/**
 * Unsubscribe a consumer. If last consumer, close the stream.
 */
function unsubscribeSharedStream(consumerId: string, accountId: number): void {
  const stream = accountStreams.get(accountId);
  if (!stream) return;

  stream.consumers.delete(consumerId);
  console.log(
    `[SharedStream] ➖ Consumer "${consumerId}" unsubscribed — ` +
    `accountId=${accountId} remaining=${stream.consumers.size}`
  );

  if (stream.consumers.size === 0) {
    stopAccountStream(stream);
  } else {
    // Restart with updated subscriptions
    scheduleStreamRestart(stream, false);
  }
}

/**
 * Force disconnect all streams (e.g., on logout).
 */
export function disconnectAllSharedStreams(): void {
  accountStreams.forEach((stream) => {
    stopAccountStream(stream);
  });
  accountStreams.clear();
  console.log('[SharedStream] 🔌 All streams disconnected');
}

